import {Fragment, useEffect, useReducer, useRef, useState} from "react";
import type {MouseEvent, RefObject} from "react";
import GlobeIcon from "../../icons/globe.svg?react";
import StarIcon from "../../icons/star.svg?react";
import UserIcon from "../../icons/user.svg?react";
import UsersIcon from "../../icons/users.svg?react";
import type {IDropdownOption} from "../../interaction/DropdownOptionsV5";
import {NavMenuStyled} from "../NavigationCommon";
import type {TMenuV5} from "../NavigationCommon";
import {NavigationMenuItem} from "../NavigationMenuItem";
import {useAppStore} from "../../../../StateManager";
import type {AppState} from "../../../../data/state/AppState";
import {MathUtils} from "../../../../utils/math/MathUtils";
import {ViewPreferenceCategory} from "../../../../generated/api/base";
import type {IViewFolder} from "../../../../data/models/ViewUtils";
import {Functions} from "../../../../utils/function/Functions";
import type {View} from "../../../../data/models/View";
import type {Report} from "../../../../data/models/Report";
import FolderPlusIcon from "../../icons/folder-plus.svg?react";
import {EventNameForViewsChangeInLocalStorage, onWorkspaceViewClick} from "../../topbar/ViewTabsCommon";
import {ButtonV5} from "../../button/ButtonV5";
import CirclePlusIcon from "../../icons/circle-plus.svg?react";
import type {ViewActionType} from "../../sharing/GlobalSharingPopup";
import {getKeyForLocalStorageWorkspaceViewSections, type OnWorkspaceRenameActiveChange, type WorkspaceViewType} from "./WorkspaceViewCommon";
import {WorkspaceViewContainer} from "./WorkspaceViewContainer";
import type {ViewFolderV5} from "./ViewFolderV5";

type WorkspaceMenuV5 = TMenuV5 & {
	readonly label: WorkspaceViewType;
};

const viewMenu: WorkspaceMenuV5[] = [
	{
		label: "Favorites",
		icon: StarIcon,
	},
	{
		label: "Global",
		icon: GlobeIcon,
	},
	{
		label: "My Views",
		icon: UserIcon,
	},
	{
		label: "Shared With Me",
		icon: UsersIcon,
	},
];

const getDropdownOptionsForSection = (
	sectionLabel: WorkspaceViewType,
	onOpenSection: (sectionLabel: WorkspaceViewType) => void,
	appState: AppState,
	lastElementInList?: RefObject<ViewFolderV5>,
): IDropdownOption[] => {
	if (sectionLabel === "Favorites" || (sectionLabel === "Global" && appState.user.isAdmin)) {
		const options: IDropdownOption[] = [
			{
				label: "New Folder",
				onClick: () => {
					const newFolder: IViewFolder = {
						name: "New Folder",
						id: MathUtils.getNewRandomGUID(),
						isOpen: false,
						children: [],
						category: ViewPreferenceCategory.Folder,
					};

					const copiedViewsArray = sectionLabel === "Favorites" ? [...appState.user.favoriteViews] : [...appState.organization.globalViews];
					copiedViewsArray.push(newFolder);

					requestAnimationFrame(() => {
						lastElementInList?.current?.setState({
							isInEditMode: true,
						});
					});

					onOpenSection(sectionLabel);

					if (sectionLabel === "Favorites") {
						return appState.user?.setFavoriteViews(copiedViewsArray);
					} else if (sectionLabel === "Global") {
						return appState.organization?.setGlobalViews(copiedViewsArray);
					}
				},
				IconComponent: FolderPlusIcon,
			},
		];

		return options;
	}

	return [];
};

interface INavigationViewSectionProps {
	readonly onlyIcon: boolean;
	readonly isHome: boolean;
	readonly openCreateViewPopup: (type: WorkspaceViewType, viewFolderId: string) => void;
	readonly onViewItemShareClick: (viewItem: View | IViewFolder) => void;
	readonly onGlobalViewActionClick: (view: View | Report, viewActionType: ViewActionType, newName?: string) => void;
	readonly onViewPopupOpenChange: (isOpen: boolean) => void;
	readonly onOpenReportPanel: (report: Report) => void;
	readonly onWorkspaceRenameActiveChange: OnWorkspaceRenameActiveChange;
	readonly searchString: string;
	readonly isFavouriteClicked?: boolean;
	readonly viewCount?: number;
	readonly floatingWorkspaceViews?: WorkspaceViewType | null;
}

export const NavigationViewSection = (props: INavigationViewSectionProps) => {
	const appState = useAppStore((state) => state.appState);
	const user = appState.user;
	const {
		openCreateViewPopup,
		onViewItemShareClick,
		onGlobalViewActionClick,
		onViewPopupOpenChange,
		onOpenReportPanel,
		onWorkspaceRenameActiveChange,
		onlyIcon,
		isHome,
		searchString,
		isFavouriteClicked,
		viewCount,
	} = props;
	const [floatingWorkspaceViews, setFloatingWorkspaceViews] = useState<WorkspaceViewType | null>(null);
	const _lastElementInList = useRef<ViewFolderV5>(null);
	const [, forceUpdate] = useReducer((x) => x + 1, 0);

	const keyForLocalStorage = getKeyForLocalStorageWorkspaceViewSections(user.id);

	let openViewSpaceTypes: WorkspaceViewType[] = JSON.parse(localStorage.getItem(keyForLocalStorage)) || null;

	if (!openViewSpaceTypes) {
		openViewSpaceTypes = ["Favorites", "Global", "My Views", "Shared With Me"];
		localStorage.setItem(keyForLocalStorage, JSON.stringify(openViewSpaceTypes));
	}

	const onOpenSection = (section: WorkspaceViewType) => {
		const newOpenViewSpaceTypes = new Set([...openViewSpaceTypes]);

		if (!newOpenViewSpaceTypes.has(section)) {
			newOpenViewSpaceTypes.add(section);
			localStorage.setItem(keyForLocalStorage, JSON.stringify([...newOpenViewSpaceTypes]));
			forceUpdate();
		}
	};

	const onCloseSection = (section: WorkspaceViewType) => {
		const newOpenViewSpaceTypes = new Set([...openViewSpaceTypes]);

		if (newOpenViewSpaceTypes.has(section)) {
			newOpenViewSpaceTypes.delete(section);
			localStorage.setItem(keyForLocalStorage, JSON.stringify([...newOpenViewSpaceTypes]));
			forceUpdate();
		}
	};

	useEffect(() => {
		const handler = () => {
			const newView = appState.actions.getReportViewById(appState.selectedReportViewId);
			onWorkspaceViewClick(newView, false);
		};

		window.addEventListener("newReportViewCreated", handler);

		return () => {
			window.removeEventListener("newReportViewCreated", handler);
		};
	}, [appState]);

	useEffect(() => {
		window.addEventListener(EventNameForViewsChangeInLocalStorage, forceUpdate);

		return () => {
			window.removeEventListener(EventNameForViewsChangeInLocalStorage, forceUpdate);
		};
	}, []);

	const filteredViewMenu = !isFavouriteClicked ? viewMenu : viewMenu.filter((menu) => menu.label === "Favorites");

	return (
		<NavMenuStyled>
			{viewCount > 0 ? (
				filteredViewMenu.map((menu, i) => {
					const onAddView = (viewFolderId: string) => {
						openCreateViewPopup(menu.label, viewFolderId);
					};

					const isGlobal = menu.label === "Global";
					const onAddViewClick =
						menu.label === "Shared With Me" || (isGlobal && !user.isAdmin)
							? undefined
							: (event: MouseEvent) => {
									event.stopPropagation();
									onAddView("");
								};

					const onAddViewToFolderClick = onAddViewClick
						? (viewFolderId: string) => {
								onAddView(viewFolderId);
							}
						: undefined;

					const showFloatingMenu = onlyIcon && floatingWorkspaceViews === menu.label;

					return (
						<Fragment key={`${menu.label}-${i}`}>
							<NavigationMenuItem
								{...menu}
								onlyIcon={onlyIcon}
								small={!isHome}
								onPlusClick={onAddViewClick}
								dropdownOptions={getDropdownOptionsForSection(menu.label, onOpenSection, appState, _lastElementInList)}
								onClick={() => (openViewSpaceTypes.includes(menu.label) ? onCloseSection(menu.label) : onOpenSection(menu.label))}
								floatingMenu={
									showFloatingMenu && (
										<WorkspaceViewContainer
											type={floatingWorkspaceViews}
											onAddClick={onAddViewToFolderClick}
											forceUpdateParent={forceUpdate}
											onShareClick={onViewItemShareClick}
											onGlobalViewActionClick={onGlobalViewActionClick}
											onViewPopupOpenChange={onViewPopupOpenChange}
											onOpenReportPanel={onOpenReportPanel}
											onWorkspaceRenameActiveChange={onWorkspaceRenameActiveChange}
											isOpen={true}
											searchString={searchString}
											lastElementInList={_lastElementInList}
										/>
									)
								}
								closeFloatingMenu={showFloatingMenu ? () => setFloatingWorkspaceViews(null) : Functions.emptyFunction}
							/>
							{!onlyIcon && (
								<WorkspaceViewContainer
									type={menu.label}
									onAddClick={onAddViewToFolderClick}
									forceUpdateParent={forceUpdate}
									onShareClick={onViewItemShareClick}
									onGlobalViewActionClick={onGlobalViewActionClick}
									onViewPopupOpenChange={onViewPopupOpenChange}
									onOpenReportPanel={onOpenReportPanel}
									onWorkspaceRenameActiveChange={onWorkspaceRenameActiveChange}
									isOpen={openViewSpaceTypes.includes(menu.label)}
									searchString={searchString}
									lastElementInList={_lastElementInList}
								/>
							)}
						</Fragment>
					);
				})
			) : (
				<>
					<div className="emptyViews">
						{isFavouriteClicked
							? "There are no favorite views."
							: "You currently have no views. Make your first view now or contact an administrator."}
					</div>

					<ButtonV5
						label="Create View"
						onClick={(event) => {
							event.stopPropagation();
							openCreateViewPopup("My Views", "");
						}}
					>
						<CirclePlusIcon />
					</ButtonV5>
				</>
			)}
		</NavMenuStyled>
	);
};
